import React, { useRef, useState, useEffect } from 'react';
import { MagnifyingGlassIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { Product } from '../data/mockData'; 
import ShapesSection from './ShapesSection';
import FrameSection from './FrameSection';
import BackgroundSection from './BackgroundSection';
import ProductsSection from './ProductsSection';
import { uploadFile, baseImageToOss, generateRandomCode } from '../utils/common';
import QRUploadModal from './QRUploadModal'; 
import { loadImagesFromAPI, ImageItem } from '../utils/imageUploadUtils';
import {OSS_BUCKET, OSS_BASE_URL} from '../config';


interface RightContentAreaProps {
  selectedTool: string;
  product: Product | undefined;
  currentProduct: Product | undefined; // 添加 currentProduct prop
  products: Product[];
  selectedLayerId: string | null;
  transformedLayers: any[];
  textContent: string;
  fontSize: number;
  fontFamily: string;
  textColor: string;
  selectedColor: string;
  selectedColorObj: any;
  onTextContentChange: (value: string) => void;
  onFontSizeChange: (value: number) => void;
  onFontFamilyChange: (value: string) => void;
  onTextColorChange: (value: string) => void;
  onSelectedColorChange: (colorId: string) => void;
  onTemplateSelect: (templateId: string) => void;
  onAddTextElement: () => void;
  onAddShapeElement: (shape: string) => void;
  onAddFrameElement: (frameStyle: string, frameColor: string, frameSize: number) => void;
  onAddImageElement?: (imageUrl: string) => void;
  onSetBackgroundTexture: (texture: string) => void;
  onSetBackgroundImage: (imageUrl: string) => void;
  onUploadBackgroundImage: (file: File) => void;
  onSetBackgroundColor: (color: string) => void;
  onSetActiveTool: (tool: string) => void;
  onLayerSelect: (layerId: string) => void;
  onTemplateUpdate?: (updatedProduct: Product) => void;
  onReorderLayers?: (fromIndex: number, toIndex: number) => void;
  onDeleteLayer?: (layerId: string) => void;
  onUpdateLayer?: (layerId: string, updatedLayer: any) => void;
  onProductSelect?: (product: Product) => void;
}

const RightContentArea: React.FC<RightContentAreaProps> = ({
  selectedTool,
  product,
  currentProduct,
  products,
  selectedLayerId,
  transformedLayers,
  textContent,
  fontSize = 50,
  fontFamily,
  textColor,
  selectedColor,
  selectedColorObj,
  onTextContentChange,
  onFontSizeChange,
  onFontFamilyChange,
  onTextColorChange,
  onSelectedColorChange,
  onTemplateSelect,
  onAddTextElement,
  onAddShapeElement,
  onAddFrameElement,
  onAddImageElement,
  onSetBackgroundTexture,
  onSetBackgroundImage,
  onUploadBackgroundImage,
  onSetBackgroundColor,
  onSetActiveTool,
  onLayerSelect,
  onReorderLayers,
  onDeleteLayer,
  onUpdateLayer,
  onProductSelect
}) => {

  const [fabricLayers, setFabricLayers] = useState<any[]>([]);
  const [fabricActive, setFabricActive] = useState<any | null>(null);

  useEffect(() => {
    const layersListener = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const objs = d.objects || [];
      console.log(objs)
      setFabricLayers(Array.isArray(objs) ? objs : []);
    };
    const activeListener = (e: Event) => {
      const d = (e as CustomEvent).detail || null;
      console.log(d)
      setFabricActive(d);
    };
    window.addEventListener('giftmkr:layersSnapshot', layersListener as EventListener);
    window.addEventListener('giftmkr:activeObjectChanged', activeListener as EventListener);
    return () => {
      window.removeEventListener('giftmkr:layersSnapshot', layersListener as EventListener);
      window.removeEventListener('giftmkr:activeObjectChanged', activeListener as EventListener);
    };
  }, []);
  
  // File upload state
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // QR Modal state
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);

  // Images state
  const [images, setImages] = useState<ImageItem[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);


  // 加载图片数据
  useEffect(() => {
    const loadImages = async () => {
      setIsLoadingImages(true);
      try {
        const result = await loadImagesFromAPI(1, '', 'all', 20);
        setImages(result.images);
      } catch (error) {
        console.error('Failed to load images:', error);
      } finally {
        setIsLoadingImages(false);
      }
    };

    loadImages();
  }, []);

  // Drag and drop state for layer reordering
  const [dragState, setDragState] = React.useState({
    isDragging: false,
    draggedIndex: -1,
    draggedOverIndex: -1
  });

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragState({
      isDragging: true,
      draggedIndex: index,
      draggedOverIndex: index
    });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragState(prev => ({
      ...prev,
      draggedOverIndex: index
    }));
  };

  // 获取文件扩展名的辅助函数
  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '.jpg'; // 默认扩展名
    }
    return filename.substring(lastDotIndex);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // 将文件转换为base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64String = event.target?.result as string;
        
        try {
          // 调用 baseImageToOss 上传到OSS，使用实际文件扩展名
          const uploadedName = await baseImageToOss(
            generateRandomCode() + getFileExtension(file.name), 
            base64String, 
            OSS_BUCKET
          );
        
          console.log('上传成功:', uploadedName);
          
          // 使用OSS返回的URL
          const uploadedUrl = `${OSS_BASE_URL}${uploadedName}`;
          
          console.log('Image uploaded and added to canvas:', uploadedUrl);
          
          // 添加图片图层到画布
          if (onAddImageElement) {
            onAddImageElement(uploadedUrl);
          }
          
        } catch (uploadError) {
          console.error('上传到OSS失败:', uploadError);
          // 如果上传失败，使用原来的 uploadFile 作为备选
          try {
            const uploadedUrl = await uploadFile(file);
            console.log('Fallback upload successful:', uploadedUrl);
            
            // 添加图片图层到画布
            if (onAddImageElement) {
              onAddImageElement(uploadedUrl);
            }
          } catch (fallbackError) {
            console.error('Fallback upload failed:', fallbackError);
            alert('Upload failed. Please try again.');
          }
        } finally {
          setIsUploading(false);
          // 清理文件输入
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      
      reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('文件处理失败:', error);
      setIsUploading(false);
      alert('File processing failed. Please try again.');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleQRUploadComplete = async (images: string[]) => {
    // 处理从 QR 码上传完成的图片
    console.log('QR upload completed:', images);
    
    // 重新加载图片列表
    try {
      setIsLoadingImages(true);
      const result = await loadImagesFromAPI(1, '', 'all', 20);
      setImages(result.images);
      console.log('Images list reloaded after QR upload');
    } catch (error) {
      console.error('Failed to reload images after QR upload:', error);
    } finally {
      setIsLoadingImages(false);
    }
    
    // 这里可以添加将图片添加到画布的逻辑
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragState.isDragging && dragState.draggedIndex !== index && onReorderLayers) {
      onReorderLayers(dragState.draggedIndex, index);
    }
    setDragState({
      isDragging: false,
      draggedIndex: -1,
      draggedOverIndex: -1
    });
  };

  const handleDragEnd = () => {
    setDragState({
      isDragging: false,
      draggedIndex: -1,
      draggedOverIndex: -1
    });
  };

  const handleProductSelect = (selectedProduct: Product) => {
    if (onProductSelect) {
      onProductSelect(selectedProduct);
      
      // Auto-select the first template if available
      if (selectedProduct.templates && selectedProduct.templates.length > 0) {
        const firstTemplate = selectedProduct.templates[0];
        onTemplateSelect(firstTemplate.id);
      }
    }
  };

  return (
    <div className="bg-gray-100 flex flex-col z-20 h-full" style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}>
      {/* Search Bar - Hide for text, frame, and layers tabs */}
      {!['text', 'frame', 'layers'].includes(selectedTool) && (
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <input
            type="text"
            placeholder="Search Elements"
            className="w-full px-4 py-2 pl-10 bg-white border border-gray-300 rounded-lg"
          />
          <div className="absolute left-3 top-2.5">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>
      )}

      {/* Content Sections */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Current Canvas Layers Section - Show when Layers is selected */}
        {selectedTool === 'layers' && (
          <div>
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Current Canvas Layers</h3>
            <div className="space-y-3"> 
              {/* Current Canvas Layers - Real-time sync */}
              <div className="bg-white border border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium text-gray-700">
                    Active Canvas Layers ({fabricLayers.length})
                    {currentProduct && currentProduct !== product && (
                      <span className="ml-2 text-xs" style={{ color: '#2e3a47' }}>(Updated)</span>
                    )}
                  </div>
                </div>
                
                {fabricLayers.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <div className="text-3xl mb-2">📄</div>
                    <div className="text-sm">No layers in canvas</div>
                    <div className="text-xs text-gray-400 mt-1">Add layers or load a template</div>
                  </div>
                ) : (
                  <div className="space-y-2">
                                        {fabricLayers.map((layer, layerIndex) => (
                      <React.Fragment key={`fabric-${layerIndex}`}>
                        {/* Drop zone indicator */}
                        {dragState.isDragging && dragState.draggedOverIndex === layerIndex && dragState.draggedIndex !== layerIndex && (
                          <div className="h-1 rounded-full mx-2" style={{ backgroundColor: '#68aeb0' }}></div>
                        )}
                        <div
                          draggable
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-move ${
                            false 
                              ? '' 
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          } ${
                            dragState.isDragging && dragState.draggedIndex === layerIndex
                              ? 'opacity-50'
                              : ''
                          } ${
                            dragState.isDragging && dragState.draggedOverIndex === layerIndex
                              ? ''
                              : ''
                          }`}
                          style={false 
                            ? { backgroundColor: '#f0f2f5', borderColor: '#2e3a47' }
                            : dragState.isDragging && dragState.draggedOverIndex === layerIndex
                            ? { borderColor: '#68aeb0', backgroundColor: '#e8f4f5' }
                            : {}}
                          onClick={() => {
                            const ev = new CustomEvent('giftmkr:selectLayerIndex', { detail: { index: layerIndex } });
                            window.dispatchEvent(ev);
                          }}
                          onDragStart={(e) => handleDragStart(e, layerIndex)}
                          onDragOver={(e) => handleDragOver(e, layerIndex)}
                          onDrop={(e) => handleDrop(e, layerIndex)}
                          onDragEnd={handleDragEnd}
                        >
              
                          
                          {/* Layer Type Icon and Number */}
                          <div className="flex flex-col items-center justify-center space-y-1">
                            {/* Layer Type Icon */}
                            <div className="w-6 h-6 flex items-center justify-center">
                              {layer.type === 'textbox' && (
                                <div className="w-4 h-4 rounded flex items-center justify-center" style={{ backgroundColor: '#68aeb0' }}>
                                  <div className="text-white text-xs font-bold">T</div>
                                </div>
                              )}
                              {['rect','circle','ellipse','polygon'].includes(layer.type) && (
                                <div className="w-4 h-4 bg-green-500 rounded flex items-center justify-center">
                                  <div className="text-white text-xs font-bold">S</div>
                                </div>
                              )}
                              {layer.type === 'image' && (
                                <div className="w-4 h-4 bg-purple-500 rounded flex items-center justify-center">
                                  <div className="text-white text-xs font-bold">I</div>
                                </div>
                              )}
                              {!['textbox', 'rect', 'circle', 'ellipse', 'polygon', 'image'].includes(layer.type) && (
                                <div className="w-4 h-4 bg-gray-500 rounded flex items-center justify-center">
                                  <div className="text-white text-xs font-bold">L</div>
                                </div>
                              )}
                            </div>
                            
                            {/* Layer Number */}
                            <div className="text-xs text-gray-400">
                              #{layerIndex + 1}
                            </div>
                          </div>
                        
                        {/* Layer Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-800 text-sm">
                            {layer.type === 'textbox' && layer.text 
                              ? layer.text.length > 20 
                                ? layer.text.substring(0, 20) + '...' 
                                : layer.text
                              : ['rect','circle','ellipse','polygon'].includes(layer.type)
                                ? `${layer.type.charAt(0).toUpperCase() + layer.type.slice(1)} Shape`
                                : `${layer.type.charAt(0).toUpperCase() + layer.type.slice(1)} Layer`
                            }
                            
                          </div>
                          
                          <div className="text-xs text-gray-500 capitalize">
                           Position: ({Math.round(layer.left || 0)}, {Math.round(layer.top || 0)}) • Size: {Math.round(((layer.width || 0) * (layer.scaleX || 1)) || (layer.radius ? layer.radius * 2 : layer.rx ? layer.rx * 2 : 0))}×{Math.round(((layer.height || 0) * (layer.scaleY || 1)) || (layer.radius ? layer.radius * 2 : layer.ry ? layer.ry * 2 : 0))}
                          </div>
                          {layer.type === 'textbox' && layer.fontSize && (
                            <div className="text-xs text-gray-400">
                              Font: {layer.fontFamily || 'Arial'} • Size: {Number(layer.fontSize).toFixed(0)}px
                            </div>
                          )}
                        </div>
                        
                        {/* Layer Status */}
                        <div className="flex items-center space-x-1">
                          {/* Delete Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const sel = new CustomEvent('giftmkr:selectLayerIndex', { detail: { index: layerIndex } });
                              window.dispatchEvent(sel);
                              const del = new CustomEvent('giftmkr:deleteActiveObject');
                              window.dispatchEvent(del);
                            }}
                            className="ml-2 p-1 hover:bg-red-100 rounded text-red-500 hover:text-red-700 transition-colors"
                            title="Delete Layer"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const sel = new CustomEvent('giftmkr:selectLayerIndex', { detail: { index: layerIndex } });
                              window.dispatchEvent(sel);
                              const isLocked = layer.locked === true;
                              const ev = new CustomEvent(isLocked ? 'giftmkr:unlockLayer' : 'giftmkr:lockLayer');
                              window.dispatchEvent(ev);
                            }}
                            className="ml-1 p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700 transition-colors"
                            title={layer.locked === true ? "Unlock Layer" : "Lock Layer"}
                          >
                            {layer.locked === true ? (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Templates Section */}
              {(currentProduct || product) && (
                <div className="mt-6">
                  <h4 className="text-md font-medium mb-3 text-gray-700">Templates</h4>
            <div className="space-y-3">
                    {(() => {
                      const activeProduct = currentProduct || product;
                      if (!activeProduct) return [];
                      
                      // 收集所有faces，去重相同的code
                      const uniqueFaces = new Map();
                      activeProduct.templates.forEach(template => {
                        if (template.design && template.design.faces) {
                          template.design.faces.forEach((face: any) => {
                            if (!uniqueFaces.has(face.code)) {
                              uniqueFaces.set(face.code, {
                                ...face,
                                templateId: template.id,
                                templateName: template.name,
                                templatePreview: template.preview
                              });
                            }
                          });
                        }
                      });
                      
                      return Array.from(uniqueFaces.values()).map((face) => (
                        <div
                          key={face.code}
                          className={`bg-white border rounded-lg p-3 cursor-pointer hover:shadow-md transition-all ${
                            currentProduct && currentProduct !== product && 
                            currentProduct.templates.find(t => t.id === face.templateId)?.design !== 
                            product?.templates.find(t => t.id === face.templateId)?.design
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-300'
                          }`}
                          style={currentProduct && currentProduct !== product && 
                            currentProduct.templates.find(t => t.id === face.templateId)?.design !== 
                            product?.templates.find(t => t.id === face.templateId)?.design
                            ? {}
                            : {}}
                          onMouseEnter={(e) => {
                            if (!(currentProduct && currentProduct !== product && 
                              currentProduct.templates.find(t => t.id === face.templateId)?.design !== 
                              product?.templates.find(t => t.id === face.templateId)?.design)) {
                              e.currentTarget.style.borderColor = '#68aeb0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!(currentProduct && currentProduct !== product && 
                              currentProduct.templates.find(t => t.id === face.templateId)?.design !== 
                              product?.templates.find(t => t.id === face.templateId)?.design)) {
                              e.currentTarget.style.borderColor = '';
                            }
                          }}
                          onClick={() => {
                            // 调用模板选择，传递对应的模板ID
                            onTemplateSelect(face.templateId);
                          }}
                        >
                          {/* Face Header */}
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                              {face.templatePreview ? (
                                <img 
                                  src={face.templatePreview} 
                                  alt={face.templateName}
                                  className="w-full h-full object-cover rounded"
                                />
                              ) : (
                                <div className="text-gray-500 text-xs">No Preview</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <div className="font-medium text-gray-800">{face.templateName}</div>
                                {currentProduct && currentProduct !== product && 
                                 currentProduct.templates.find(t => t.id === face.templateId)?.design !== 
                                 product?.templates.find(t => t.id === face.templateId)?.design && (
                                  <div className="w-2 h-2 bg-green-500 rounded-full" title="Template has been updated"></div>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 capitalize">Face: {face.code}</div>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                      </div>
                    </div>
                  )}
            </div>
          </div>
        )}

        {/* Uploads Section - Show when Uploads is selected */}
        {selectedTool === 'uploads' && (
          <div>
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Upload Images</h3>
            
            <div className="space-y-4">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              
              {/* Upload Buttons */}
              <div className="space-y-3">
                <button 
                  onClick={handleUploadClick}
                  disabled={isUploading}
                  className="w-full px-4 py-3 text-white rounded-lg transition-colors flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#68aeb0' }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = '#7bb8ba';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = '#68aeb0';
                    }
                  }}
                >
                  {isUploading ? (
                    <>
                      <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload Images
                    </>
                  )}
                </button>
                
                <button 
                  onClick={() => setIsQRModalOpen(true)}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Upload from Your Photos
                </button>
              </div>
              
              {/* Image List */}
              <div>
                <h4 className="text-md font-medium mb-3 text-gray-700">Recent Images</h4>
                <div className="grid grid-cols-2 gap-3">
                  {/* 从接口加载的图片 */}
                  {isLoadingImages ? (
                    // 加载状态
                    Array.from({ length: 6 }).map((_, index) => (
                      <div key={index} className="bg-gray-200 rounded-lg p-2 animate-pulse">
                        <div className="w-full h-20 bg-gray-300 rounded mb-2"></div>
                        <div className="h-3 bg-gray-300 rounded"></div>
                      </div>
                    ))
                  ) : images.length > 0 ? (
                    // 显示真实图片
                    images.slice(0, 6).map((image) => (
                      <div 
                        key={image.id} 
                        className="bg-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-300 transition-colors"
                        onClick={() => onAddImageElement && onAddImageElement(image.url || '')}
                      >
                        <div className="w-full h-20 rounded mb-2 overflow-hidden bg-white flex items-center justify-center">
                          {image.url ? (
                            <img 
                              src={image.url} 
                              alt={image.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <PhotoIcon className={`w-8 h-8 text-gray-400 ${image.url ? 'hidden' : ''}`} />
                        </div>
                        <div className="text-xs text-gray-600 truncate" title={image.name}>
                          {image.name}
                        </div>
                      </div>
                    ))
                  ) : (
                    // 没有图片时的占位符
                    Array.from({ length: 6 }).map((_, index) => (
                      <div key={index} className="bg-gray-200 rounded-lg p-2">
                        <div className="w-full h-20 bg-gray-300 rounded mb-2 flex items-center justify-center">
                          <PhotoIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className="text-xs text-gray-500">No images</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Upload Progress (hidden by default) */}
              <div className="hidden">
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Uploading...</span>
                    <span className="text-sm text-gray-500">75%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="h-2 rounded-full" style={{ width: '75%', backgroundColor: '#68aeb0' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Text Section - Show when Text is selected */}
        {selectedTool === 'text' && (
          <div>
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {fabricActive && fabricActive.type === 'textbox' ? 'Edit Text' : 'Add Text'}
            </h3>
            
            <div className="space-y-4">
              {/* Text Content */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Text Content</label>
                <textarea
                  value={textContent}
                  onChange={(e) => onTextContentChange(e.target.value)}
                  placeholder="Enter your text here..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                  rows={3}
                />
              </div>
                 
              
              {/* Add Text Button */}
              <button
                onClick={() => {
                  onAddTextElement();
                  onSetActiveTool('select');
                }}
                className="w-full px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#68aeb0' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
              >
                {fabricActive && fabricActive.type === 'textbox' ? 'Update Text' : 'Add Text'}
              </button>
            </div>
          </div>
        )}

        {/* Shapes Section - Show when Shape is selected */}
        {selectedTool === 'shape' && (
          <ShapesSection onAddShapeElement={onAddShapeElement} />
        )}

        {/* Frame Section - Show when Frame is selected */}
        {selectedTool === 'frame' && (
          <FrameSection 
            onAddFrameElement={onAddFrameElement}
            onSetActiveTool={onSetActiveTool}
          />
        )}

        {/* Background Section - Show when Background is selected */}
        {selectedTool === 'background' && (
          <BackgroundSection
            product={product}
            selectedColor={selectedColor}
            selectedColorObj={selectedColorObj}
            onSelectedColorChange={onSelectedColorChange}
            onSetBackgroundTexture={onSetBackgroundTexture}
            onSetBackgroundImage={onSetBackgroundImage}
            onUploadBackgroundImage={onUploadBackgroundImage}
            onSetBackgroundColor={onSetBackgroundColor}
          />
        )}

        {/* Products Section - Show when Products is selected */}
        {selectedTool === 'products' && (
          <ProductsSection
            products={products}
            currentProduct={currentProduct}
            onProductSelect={handleProductSelect}
          />
        )}

       
      </div>
      
      {/* QR Upload Modal */}
      <QRUploadModal
        isOpen={isQRModalOpen}
        onClose={() => setIsQRModalOpen(false)}
        onUploadComplete={handleQRUploadComplete}
      />

    </div>
  );
};

export default RightContentArea; 
